<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToMsg'
});
</script>
<template>
	<div ref="el" :key="langKey" :flex="flex ? flex : null" class="to-msg" :class="setClass">
		<div class="to-msg-bg" @click="bgClose"></div>
		<div class="to-msg-scroll">
			<div class="to-msg-wrapper" :style="setWrapperStyle">
				<div class="to-msg-side">
					<component :is="emoteComponent" v-if="emoteComponent"></component>
					<div class="to-msg-icon"></div>
				</div>
				<div v-if="hasInfoContent" class="to-msg-main">
					<div class="to-msg-text">
						<div v-if="title || slots.title || slots.default" class="to-msg-title">
							{{ title }}
							<slot name="title" />
						</div>
						<div v-if="content || slots.content" class="to-msg-content">
							<slot name="content">
								<component :is="content" v-if="isCom"></component>
								<span v-else v-html="safeContent"></span>
							</slot>
						</div>
					</div>
					<div v-if="slots.detail || detail" class="to-msg-detail">
						<template v-if="$slots.detail || detail">
							<to-button v-if="!iShowDetail" icon-position="right" icon="expand" class="to-msg-detail-button" href="javascript:void(0)" @click="iShowDetail = true">{{ t('to.msg.showDetail') }}</to-button>
							<to-button v-else icon-position="right" icon="collapse" class="to-msg-detail-button" href="javascript:void(0)" @click="iShowDetail = false">{{ t('to.msg.hideDetail') }}</to-button>
						</template>
						<div v-if="slots.detail && iShowDetail" class="to-msg-detail-content">
							<slot name="detail" />
						</div>
						<div v-else-if="detail && iShowDetail" class="to-msg-detail-content" v-html="safeDetail" />
					</div>
					<div v-if="confirmable || cancelable || buttons.length" class="to-msg-fn">
						<slot>
							<to-button v-for="(btn, index) in buttons" :key="index" class="to-msg-button type-extend" v-bind="btn.attrs" @click="handleButtonClick(btn)">{{ btn.label }}</to-button>
							<to-button v-if="cancelText || true" class="to-msg-button type-extend" @click="cancel">{{ cancelText || t('to.msg.cancel') }}</to-button>
							<to-button v-if="confirmText || true" class="to-msg-button type-confirm" @click="confirm">{{ confirmText || t('to.msg.confirm') }}</to-button>
						</slot>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import DOMPurify from 'dompurify'
import { computed, ref, useSlots, useAttrs, CSSProperties, isVNode, watch } from 'vue'
import { ToButton } from '@touchvue/components'
import { t, langKey } from '@touchvue/locale'


export interface ButtonConfig {
	label: string
	attrs?: Record<string, any>
	fn?: () => void
}

export interface Props {
	flex?: boolean
	type?: string
	mode?: string
	title?: string
	content?: any
	buttons?: ButtonConfig[]
	height?: string | number
	width?: string | number
	dir?: string
	detail?: string
	showDetail?: boolean
	padding?: string | number
	position?: string
	confirmText?: string
	cancelText?: string
	closeOnBlur?: boolean
	zIndex?: number | string
	show?: boolean
	global?: boolean
	confirmable?: boolean
	cancelable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	mode: 'default',
	title: '',
	content: '',
	buttons: () => [],
	height: 'auto',
	width: '',
	flex: false,
	detail: '',
	showDetail: true,
	padding: 0,
	type: '',
	dir: '',
	position: '',
	confirmText: '',
	cancelText: '',
	closeOnBlur: true,
	zIndex: 0,
	show: false,
	global: true,
	confirmable: true,
	cancelable: true
})

const emit = defineEmits(['close', 'confirm'])

const el = ref(null)
const iShowDetail = ref(props.showDetail)
const visible = ref(props.show)
const slots = useSlots()
const isCom = isVNode(props.content)

const className = useAttrs().class as string | undefined

// 监听 show 属性变化
watch(
	() => props.show,
	newVal => {
		visible.value = newVal
	},
	{
		immediate: true
	}
)

// 计算属性

const setClass = computed(() => {
	const arr: string[] = []
	if (props.mode) arr.push(`mode-${props.mode}`)
	if (props.type) {
		arr.push(`type-${props.type}`)
	}
	if (props.dir) {
		arr.push(`dir-${props.dir}`)
	}
	if (props.position) {
		arr.push(`position-${props.position}`)
	}
	if (props.global) {
		arr.push('is-global')
	}
	if (visible.value) arr.push('is-show')
	if (className) arr.push(className)
	return arr
})

const setWrapperStyle = computed(() => {
	const style: CSSProperties = {}
	if (props.width) {
		style.width = `${props.width}em`
	}
	return style
})

const hasInfoContent = computed(() => props.title || slots.default || props.content || slots.content || slots.title)
const safeContent = computed(() => DOMPurify.sanitize(props.content))
const safeDetail = computed(() => DOMPurify.sanitize(props.detail))

const emoteComponent = computed(() => {
	return (window as any).$TouchUI?.msg?.emote?.[props.type || 'default']
})

const handleButtonClick = (btn: ButtonConfig) => {
	if (typeof btn.fn === 'function') {
		btn.fn()
	}
}

const open = () => {
	const el = document.querySelector('.to-msg') as HTMLElement
	if (props.zIndex) {
		el.style.zIndex = props.zIndex.toString()
	} else {
		el.style.zIndex = ((window as any).$TouchUI?.zIndex ?? 1000).toString()
		if ((window as any).$TouchUI) (window as any).$TouchUI.zIndex++
	}
	visible.value = true
}

const close = () => {
	visible.value = false
	emit('close')
}

const confirm = () => {
	emit('confirm')
	visible.value = false
	emit('close')
}

const cancel = () => {
	visible.value = false
	emit('close')
}

const bgClose = () => {
	if (props.closeOnBlur) {
		close()
	}
}

defineExpose({ open, close })
</script>
